import { format } from 'date-fns'
import { uniq } from 'lodash-es'

import {
  PROGRAM_TAXONOMY,
  ProgramCustomerPropertyId,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import { CoachingSessionTypes, LTSessionTypes } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import {
  attendanceValues,
  diagnoses,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { LTProgramName } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

export const MILITARY_ONE_SOURCE_EMPLOYER = 'militaryonesource'

export const MOS_SESSION_LABEL = 'non-medical counseling'

export const ROLES = {
  PRACTICES_ADMIN: 'ROLE_practice_admin',
  PAYMENTS_ADMIN: 'ROLE_payment_admin',
  PROVIDERS_ADMIN: 'ROLE_provider_admin',
  RECONCILER: 'ROLE_reconciler',
  NP_ADMIN: 'ROLE_np_admin',
  VIEW_ONLY: 'ROLE_view_only',
  LT_SUPERVISOR: 'ROLE_lt_supervisor',
  LT_PRESCRIBER: 'ROLE_lt_prescriber',
  LT_PRESCRIBER_SUPERVISOR: 'ROLE_lt_prescriber_supervisor',
  LT_THERAPIST: 'ROLE_lt_therapist',
  LT_COACH: 'ROLE_lt_coach',
  LT_COACH_SUPERVISOR: 'ROLE_lt_coach_supervisor',
  LT_SS_COACH: 'ROLE_lt_ss_coach',
  LT_SS_COACH_SUPERVISOR: 'ROLE_lt_ss_coach_supervisor',
  LT_AUD_THERAPIST_SUPERVISOR: 'ROLE_lt_aud_therapist_supervisor',
  LT_AUD_THERAPIST: 'ROLE_lt_aud_therapist',
  INDIVIDUAL_PROVIDER: 'ROLE_provider',
  PRACTICE_PROVIDER: 'ROLE_practice_provider',
  LT_FELLOW_SUPERVISOR: 'ROLE_lt_fellow_supervisor',
  LT_FELLOW_THERAPIST: 'ROLE_lt_fellow_therapist',
  ICAS_PROVIDER: 'ROLE_icas_therapist',
  ICAS_INDIVIDUAL_THERAPIST: 'ROLE_icas_individual_therapist',
  LT_CLINICAL_LEAVE_EVALUATOR: 'ROLE_lt_clinical_leave_evaluator',
  LT_CLINICAL_LEAVE_EVALUATOR_SUPERVISOR: 'ROLE_lt_clinical_leave_evaluator_supervisor',
  LT_SAP_EVALUATOR: 'ROLE_lt_sap_evaluator',
  LT_TEENS_THERAPIST_SUPERVISOR: 'ROLE_lt_teens_therapist_supervisor',
  LT_TEENS_THERAPIST: 'ROLE_lt_teens_therapist',
  LT_DBT_THERAPIST_SUPERVISOR: 'ROLE_lt_dbt_therapist_supervisor',
  LT_DBT_THERAPIST: 'ROLE_lt_dbt_therapist',
  LT_DBT_TEENS_THERAPIST_SUPERVISOR: 'ROLE_lt_dbt_teens_therapist_supervisor',
  LT_DBT_TEENS_THERAPIST: 'ROLE_lt_dbt_teens_therapist',
  LT_CFP_COACH: 'ROLE_lt_cfp_coach',
  LT_NSC_COACH: 'ROLE_lt_nsc_coach',
  LT_NSC_COACH_SUPERVISOR: 'ROLE_lt_nsc_coach_supervisor',
  LT_CGT_COACH: 'ROLE_lt_cgt_coach',
  DA_COUPLES_THERAPIST: 'ROLE_da_couples_therapist',
  DA_FAMILY_THERAPIST: 'ROLE_da_family_therapist',
  DA_INDIVIDUAL_THERAPIST: 'ROLE_da_individual_therapist',
  DA_CHILD_THERAPIST: 'ROLE_da_child_therapist',
  DA_ADOLESCENT_THERAPIST: 'ROLE_da_adolescent_therapist',
  DA_INDIVIDUAL_SUPERVISOR: 'ROLE_da_individual_therapist_supervisor',
  DA_FAMILY_SUPERVISOR: 'ROLE_da_family_therapist_supervisor',
  DA_COUPLES_SUPERVISOR: 'ROLE_da_couples_therapist_supervisor',
  DA_CHILD_SUPERVISOR: 'ROLE_da_child_therapist_supervisor',
  DA_ADOLESCENT_SUPERVISOR: 'ROLE_da_adolescent_therapist_supervisor',
  // Bend roles (NP and Core providers)
  DA_CORECARE_COORDINATOR: 'ROLE_da_corecare_coordinator',
  LT_CORECARE_COORDINATOR: 'ROLE_lt_corecare_coordinator',
  DA_NPTESTER: 'ROLE_da_nptester',
  LT_NPTESTER: 'ROLE_lt_nptester',
  // NP provider roles, from the NPTesting CTPO partner plus the PSS care provider role
  LT_NP_TESTING_PSYCHOLOGIST: 'ROLE_lt_np_testing_psychologist',
  LT_NP_NEUROPSYCHOLOGIST: 'ROLE_lt_np_neuropsychologist',
  LT_NP_PSYCHOMETRIST: 'ROLE_lt_np_psychometrist',
  LT_NP_CARE_COORDINATOR: 'ROLE_lt_np_care_coordinator',
  DA_NP_TESTING_PSYCHOLOGIST: 'ROLE_da_np_testing_psychologist',
  DA_NP_NEUROPSYCHOLOGIST: 'ROLE_da_np_neuropsychologist',
  DA_NP_PSYCHOMETRIST: 'ROLE_da_np_psychometrist',
  DA_NP_CARE_COORDINATOR: 'ROLE_da_np_care_coordinator',
  // Bend supervisor roles
  NPTESTER_SUPERVISOR: 'ROLE_nptester_supervisor',
  CORECARE_COORDINATOR_SUPERVISOR: 'ROLE_corecare_coordinator_supervisor',
  // Manager coaching roles
  LT_MANAGER_COACH: 'ROLE_lt_mc_coach',
  LT_MANAGER_COACH_SUPERVISOR: 'ROLE_lt_mc_coach_supervisor',
}

export const NP_TESTER_ROLES = [
  ROLES.DA_NPTESTER,
  ROLES.LT_NPTESTER,
  ROLES.LT_NP_TESTING_PSYCHOLOGIST,
  ROLES.LT_NP_NEUROPSYCHOLOGIST,
  ROLES.DA_NP_TESTING_PSYCHOLOGIST,
  ROLES.DA_NP_NEUROPSYCHOLOGIST,
  ROLES.LT_NP_PSYCHOMETRIST,
  ROLES.LT_NP_CARE_COORDINATOR,
  ROLES.DA_NP_PSYCHOMETRIST,
  ROLES.DA_NP_CARE_COORDINATOR,
]

export const BEND_ROLES = [ROLES.DA_CORECARE_COORDINATOR, ROLES.LT_CORECARE_COORDINATOR, ...NP_TESTER_ROLES]

export const SUPERVISOR_ROLES = [
  ROLES.LT_SUPERVISOR,
  ROLES.LT_PRESCRIBER_SUPERVISOR,
  ROLES.LT_COACH_SUPERVISOR,
  ROLES.LT_SS_COACH_SUPERVISOR,
  ROLES.LT_NSC_COACH_SUPERVISOR,
  ROLES.LT_AUD_THERAPIST_SUPERVISOR,
  ROLES.LT_FELLOW_SUPERVISOR,
  ROLES.LT_TEENS_THERAPIST_SUPERVISOR,
  ROLES.LT_DBT_THERAPIST_SUPERVISOR,
  ROLES.LT_DBT_TEENS_THERAPIST_SUPERVISOR,
  ROLES.DA_INDIVIDUAL_SUPERVISOR,
  ROLES.DA_FAMILY_SUPERVISOR,
  ROLES.DA_COUPLES_SUPERVISOR,
  ROLES.DA_CHILD_SUPERVISOR,
  ROLES.DA_ADOLESCENT_SUPERVISOR,
  ROLES.NPTESTER_SUPERVISOR,
  ROLES.CORECARE_COORDINATOR_SUPERVISOR,
  ROLES.LT_MANAGER_COACH_SUPERVISOR,
]

export const BCT_ROLES = [
  ROLES.LT_THERAPIST,
  ROLES.LT_SUPERVISOR,
  ROLES.LT_FELLOW_THERAPIST,
  ROLES.LT_FELLOW_SUPERVISOR,
  ROLES.LT_TEENS_THERAPIST,
  ROLES.LT_TEENS_THERAPIST_SUPERVISOR,
  ROLES.DA_COUPLES_THERAPIST,
  ROLES.DA_FAMILY_THERAPIST,
  ROLES.DA_INDIVIDUAL_THERAPIST,
  ROLES.DA_CHILD_THERAPIST,
  ROLES.DA_ADOLESCENT_THERAPIST,
  ROLES.LT_DBT_THERAPIST,
  ROLES.LT_DBT_THERAPIST_SUPERVISOR,
  ROLES.LT_DBT_TEENS_THERAPIST,
  ROLES.LT_DBT_TEENS_THERAPIST_SUPERVISOR,
]

export const LT_COACH_ROLES = [
  ROLES.LT_COACH,
  ROLES.LT_SS_COACH,
  ROLES.LT_CFP_COACH,
  ROLES.LT_NSC_COACH,
  ROLES.LT_CGT_COACH,
  ROLES.LT_MANAGER_COACH,
]

export const BCC_COACH_SUPERVISOR_ROLES = [
  ROLES.LT_COACH_SUPERVISOR,
  ROLES.LT_SS_COACH_SUPERVISOR,
  ROLES.LT_NSC_COACH_SUPERVISOR,
  ROLES.LT_MANAGER_COACH_SUPERVISOR,
]

export const BCC_ROLES = [...LT_COACH_ROLES, ...BCC_COACH_SUPERVISOR_ROLES]

export const DA_ROLES = [
  ROLES.DA_INDIVIDUAL_THERAPIST,
  ROLES.DA_COUPLES_THERAPIST,
  ROLES.DA_FAMILY_THERAPIST,
  ROLES.DA_CHILD_THERAPIST,
  ROLES.DA_ADOLESCENT_THERAPIST,
  ROLES.DA_INDIVIDUAL_SUPERVISOR,
  ROLES.DA_FAMILY_SUPERVISOR,
  ROLES.DA_COUPLES_SUPERVISOR,
  ROLES.DA_CHILD_SUPERVISOR,
  ROLES.DA_ADOLESCENT_SUPERVISOR,
]

export const employmentTypes = {
  FULL_TIME: 'FT',
  PART_TIME: 'PT',
  FLEX: 'Flex',
  ENHANCED_PART_TIME: 'ePT',
  REDUCED_PART_TIME: 'rPT',
  FULL_TIME_REDUCED: 'FT-R',
  REDUCED_FULL_TIME: 'FT-R',
  CLINICAL_QUALITY_CONSULTANTS: 'CQC',
  CLINICAL_QUALITY_COORDINATOR: 'CQC',
  FELLOW_SUPERVISOR: 'LCF Supervisor',
  TEST: 'test',
}

// NOTE: Role configs have been moved to common/roleConfigs/
// Import { roleConfigs } from '../common/roleConfigs' instead

export type ProgramConfig = {
  activityActions: {
    copy: boolean
    edit: boolean
    move: boolean
    unassign: boolean
  }
  aiNotesEnabled: boolean
  appointmentDurationOptions: number[]
  autoAssignOnSessionClose: boolean
  canPlanFutureAssignments: boolean
  oldClientIntake?: string
  clientIntake: string
  clientIntakeNonUS?: string
  defaultAppointmentDuration: number
  displayCloseEpisodeBtn: boolean
  enhancedLCxDiagnosisEnabled: boolean
  hideSessionsPageScheduleButton?: boolean
  icon: string
  maxCapacity: number
  programBookingLinksAllowed: string[]
  programGuidanceURL?: string
  programGuidelinesURL?: string
  programsToDisplay?: ProgramCustomerPropertyId[]
  programTitleOverride?: { [key in ProgramCustomerPropertyId]?: string }
  combineProgramSectionsAndShowReferral?: boolean
  recommendedEpisodeLength: number | null
  sessionClosureGuide?: string
  sessionFrequencyOptions?: { label: string; value: number }[]
  sessionLabel: string
  supportsMultipleAssessmentRecipients?: boolean
  shouldConfirmSameSessionAssignment: boolean
  showClientRelationshipFilter?: boolean
  showSessionLimit: boolean
}

enum SESSION_FREQUENCY {
  NO_REPEAT = 'no repeat',
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
}

export const sessionFrequencyOptions = {
  [SESSION_FREQUENCY.NO_REPEAT]: { label: 'Does not repeat', value: 0 },
  [SESSION_FREQUENCY.WEEKLY]: { label: 'Repeat every week', value: 7 },
  [SESSION_FREQUENCY.BIWEEKLY]: { label: 'Repeat every two weeks', value: 14 },
}

// Used by NSC/CGT roles so they can copy any configured program's link.
// Display is still bounded by coverage/programsToDisplay.
const ALL_PROGRAM_BOOKING_LINKS: ProgramCustomerPropertyId[] = [
  ProgramCustomerPropertyId.considerLyraTherapy, // Blended Care Therapy
  ProgramCustomerPropertyId.dialecticalBehaviorTherapy, // DBT
  ProgramCustomerPropertyId.alcoholUseDisorderTherapy, // AUD / Renew
  ProgramCustomerPropertyId.stressManagement, // Coaching
  ProgramCustomerPropertyId.coachingForParents, // Coaching for Parents
  ProgramCustomerPropertyId.guidedSelfCareEnabled, // Single Session Coaching
  ProgramCustomerPropertyId.blendedCareMeds, // Medication Management
  ProgramCustomerPropertyId.clinicalLeaveEvaluation, // Clinical Leave Evaluation
  ProgramCustomerPropertyId.substanceUseEvaluation, // Substance Use Evaluation (SAP)
  ProgramCustomerPropertyId.directAccessTherapy, // Direct Access Therapy
  ProgramCustomerPropertyId.considerMeds, // Direct Access Meds
  ProgramCustomerPropertyId.nicotineQuitline, // Nicotine Quitline
  ProgramCustomerPropertyId.neurodiversitySkillsCoaching, // Neurodiversity Skills Coaching
  ProgramCustomerPropertyId.caregiverTrainingForNeuro, // Caregiver Training for Neurodiversity
]

export const programConfig: Record<LTProgramName, ProgramConfig> = {
  Coaching: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [45],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    oldClientIntake: 'coachingClientIntake',
    clientIntake: 'bccClientIntake',
    clientIntakeNonUS: 'bccClientIntakeNonUS',
    defaultAppointmentDuration: 45,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    // We pass a string of the icon component name here in order to be able to import the programConfig in the integration tests to make assertions.
    // Setting the icon component directly makes importing programConfig from the tests impossible as it would
    // require to have webpack and appropriate loaders. It would also slow down the tests.
    icon: 'CoachingIcon',
    maxCapacity: 13,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.guidedSelfCareEnabled,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.coachingForParents,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
      ProgramCustomerPropertyId.caregiverTrainingForNeuro,
    ],
    programGuidanceURL: 'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/360044619034',
    programGuidelinesURL:
      'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/360045123993-Care-Referral-Based-on-Coaching-Conversation&sa=D&source=docs&ust=1648768380656279&usg=AOvVaw0TDydMJE6eH5IB3_imqvtC',
    recommendedEpisodeLength: 12,
    sessionClosureGuide:
      'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/13446360980883-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  SingleSessionCoaching: {
    activityActions: {
      copy: false,
      edit: true,
      move: false,
      unassign: false,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: false,
    // When set to true, the provider can create and configure activities for future sessions in the curriculum. They will be assigned to the client (hence visible to them) only when they get to the given session. When set to false, the provider can only configure and assign actitivites directly, so they will be visible to the client right away.
    canPlanFutureAssignments: false,
    clientIntake: 'singleSessionCoachingClientIntake',
    oldClientIntake: 'sscClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: true,
    enhancedLCxDiagnosisEnabled: false,
    // We pass a string of the icon component name here in order to be able to import the programConfig in the integration tests to make assertions.
    // Setting the icon component directly makes importing programConfig from the tests impossible as it would
    // require to have webpack and appropriate loaders. It would also slow down the tests.
    icon: 'SingleSessionCoachingIcon',
    maxCapacity: 13,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.guidedSelfCareEnabled,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.coachingForParents,
      ProgramCustomerPropertyId.nicotineQuitline,
    ],
    recommendedEpisodeLength: 6,
    sessionClosureGuide:
      'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/13446360980883-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Week',
    shouldConfirmSameSessionAssignment: false,
    showSessionLimit: true,
  },
  CoachingForParents: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [45],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    oldClientIntake: 'coachingClientIntake',
    clientIntake: 'bccClientIntake',
    clientIntakeNonUS: 'bccClientIntakeNonUS',
    defaultAppointmentDuration: 45,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    icon: 'CoachingIcon',
    maxCapacity: 13,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.guidedSelfCareEnabled,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.coachingForParents,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
      ProgramCustomerPropertyId.caregiverTrainingForNeuro,
    ],
    programGuidanceURL: 'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/360044619034',
    programGuidelinesURL:
      'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/360045123993-Care-Referral-Based-on-Coaching-Conversation&sa=D&source=docs&ust=1648768380656279&usg=AOvVaw0TDydMJE6eH5IB3_imqvtC',
    recommendedEpisodeLength: 12,
    sessionClosureGuide:
      'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/13446360980883-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  ManagerCoaching: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [45],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    oldClientIntake: 'coachingClientIntake',
    clientIntake: 'mcClientIntake',
    defaultAppointmentDuration: 45,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    icon: 'CoachingIcon',
    maxCapacity: 13,
    programBookingLinksAllowed: [ProgramCustomerPropertyId.managerCoaching],
    programGuidanceURL: 'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/360044619034',
    programGuidelinesURL:
      'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/360045123993-Care-Referral-Based-on-Coaching-Conversation&sa=D&source=docs&ust=1648768380656279&usg=AOvVaw0TDydMJE6eH5IB3_imqvtC',
    recommendedEpisodeLength: 8,
    sessionClosureGuide:
      'https://coaching-lyrahealth.zendesk.com/hc/en-us/articles/13446360980883-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  BlendedCareTherapy: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    oldClientIntake: 'clientIntake',
    clientIntake: 'bctClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: true,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.coachingForParents,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
      ProgramCustomerPropertyId.caregiverTrainingForNeuro,
    ],
    programGuidanceURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    programGuidelinesURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    recommendedEpisodeLength: 8,
    sessionClosureGuide: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  TeensTherapy: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [25, 50, 80],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    oldClientIntake: 'clientIntake',
    clientIntake: 'lcttClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: true,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
      ProgramCustomerPropertyId.caregiverTrainingForNeuro,
    ],
    programGuidanceURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    programGuidelinesURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    recommendedEpisodeLength: 8,
    sessionClosureGuide: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  DialecticalBehaviorTherapy: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    clientIntake: 'dbtClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: true,
    icon: 'DialecticalBehavioralTherapyIcon',
    maxCapacity: 28,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.coachingForParents,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
      ProgramCustomerPropertyId.caregiverTrainingForNeuro,
    ],
    programGuidanceURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    programGuidelinesURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    recommendedEpisodeLength: 8,
    sessionClosureGuide: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  DialecticalBehaviorTherapyTeens: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [30, 50, 90],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    supportsMultipleAssessmentRecipients: true,
    clientIntake: 'dbttTeenIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: true,
    icon: 'DialecticalBehavioralTherapyIcon',
    maxCapacity: 28,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.dialecticalBehaviorTherapyTeens,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.coachingForParents,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
      ProgramCustomerPropertyId.caregiverTrainingForNeuro,
    ],
    programGuidanceURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    programGuidelinesURL: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843/',
    recommendedEpisodeLength: 8,
    sessionClosureGuide: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showClientRelationshipFilter: true,
    showSessionLimit: false,
  },
  NeurodiversitySkillsCoaching: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [45],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    clientIntake: 'nscClientIntake',
    defaultAppointmentDuration: 45,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    icon: 'CoachingIcon',
    maxCapacity: 13,
    programBookingLinksAllowed: ALL_PROGRAM_BOOKING_LINKS,
    recommendedEpisodeLength: 12,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  CaregiverTrainingForNeuro: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [45],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    clientIntake: 'cgtClientIntake',
    defaultAppointmentDuration: 45,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    icon: 'CoachingIcon',
    maxCapacity: 13,
    programBookingLinksAllowed: ALL_PROGRAM_BOOKING_LINKS,
    recommendedEpisodeLength: 12,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  MedicationManagement: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [25, 50, 80],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    oldClientIntake: 'medsClientIntake',
    clientIntake: 'bcmClientIntake',
    defaultAppointmentDuration: 25,
    icon: 'BlendedCareMedicineIcon',
    maxCapacity: 20,
    recommendedEpisodeLength: null,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.neurodiversitySkillsCoaching,
      ProgramCustomerPropertyId.caregiverTrainingForNeuro,
    ],
    programGuidanceURL: 'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/4407784647315',
    programGuidelinesURL: 'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: true,
  },
  AlcoholUseDisorderTherapy: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [25, 50, 80],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    clientIntake: 'audSudExpansionClientIntake',
    oldClientIntake: 'audClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: true,
    icon: 'AlcoholUseDisorderTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.coachingForParents,
      ProgramCustomerPropertyId.nicotineQuitline,
    ],
    recommendedEpisodeLength: 16,
    sessionClosureGuide: 'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/5383959501843-Session-Closure-Guide',
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: false,
  },
  ClinicalLeaveEvaluation: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [30, 50],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    clientIntake: 'cleClientIntake',
    defaultAppointmentDuration: 30,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    icon: 'ClinicalLeaveEvaluationIcon',
    maxCapacity: 20,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.substanceUseEvaluation,
    ],
    recommendedEpisodeLength: 3,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: true,
  },
  SubstanceUseEvaluation: {
    activityActions: {
      copy: true,
      edit: true,
      move: true,
      unassign: true,
    },
    aiNotesEnabled: true,
    appointmentDurationOptions: [30, 50],
    autoAssignOnSessionClose: true,
    canPlanFutureAssignments: true,
    clientIntake: 'sapClientIntake',
    defaultAppointmentDuration: 30,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    icon: 'ClinicalLeaveEvaluationIcon',
    maxCapacity: 20,
    programBookingLinksAllowed: [
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.nicotineQuitline,
      ProgramCustomerPropertyId.substanceUseEvaluation,
    ],
    recommendedEpisodeLength: 3,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    shouldConfirmSameSessionAssignment: true,
    showSessionLimit: true,
  },
  DirectAccessIndividualTherapy: {
    activityActions: {
      copy: false,
      edit: false,
      move: false,
      unassign: false,
    },
    aiNotesEnabled: false,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: false,
    canPlanFutureAssignments: false,
    clientIntake: 'daIndividualClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    hideSessionsPageScheduleButton: false,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [],
    programsToDisplay: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
    ],
    programTitleOverride: {
      [ProgramCustomerPropertyId.alcoholUseDisorderTherapy]: 'Renew for Alcohol/Substance Use Disorder',
    },
    combineProgramSectionsAndShowReferral: true,
    recommendedEpisodeLength: 10,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    shouldConfirmSameSessionAssignment: false,
    showSessionLimit: false,
  },
  // International Engage (NETEN-2641). Mirrors DirectAccessIndividualTherapy minus US-only referral
  // sections; final values tracked in NETEN-2653.
  IcasIndividualTherapy: {
    activityActions: {
      copy: false,
      edit: false,
      move: false,
      unassign: false,
    },
    aiNotesEnabled: false,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: false,
    canPlanFutureAssignments: false,
    clientIntake: 'daIndividualClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    hideSessionsPageScheduleButton: false,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [],
    recommendedEpisodeLength: null,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    shouldConfirmSameSessionAssignment: false,
    showSessionLimit: false,
  },
  DirectAccessCouplesTherapy: {
    activityActions: {
      copy: false,
      edit: false,
      move: false,
      unassign: false,
    },
    aiNotesEnabled: false,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: false,
    canPlanFutureAssignments: false,
    clientIntake: 'daCouplesClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    hideSessionsPageScheduleButton: false,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [],
    programsToDisplay: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
    ],
    programTitleOverride: {
      [ProgramCustomerPropertyId.alcoholUseDisorderTherapy]: 'Renew for Alcohol/Substance Use Disorder',
    },
    combineProgramSectionsAndShowReferral: true,
    recommendedEpisodeLength: 10,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    shouldConfirmSameSessionAssignment: false,
    showSessionLimit: false,
  },
  DirectAccessFamilyTherapy: {
    activityActions: {
      copy: false,
      edit: false,
      move: false,
      unassign: false,
    },
    aiNotesEnabled: false,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: false,
    canPlanFutureAssignments: false,
    clientIntake: 'daFamilyClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    hideSessionsPageScheduleButton: false,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [],
    programsToDisplay: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
    ],
    programTitleOverride: {
      [ProgramCustomerPropertyId.alcoholUseDisorderTherapy]: 'Renew for Alcohol/Substance Use Disorder',
    },
    combineProgramSectionsAndShowReferral: true,
    recommendedEpisodeLength: 10,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    shouldConfirmSameSessionAssignment: false,
    showSessionLimit: false,
  },
  DirectAccessIndividualAdolescentTherapy: {
    activityActions: {
      copy: false,
      edit: false,
      move: false,
      unassign: false,
    },
    aiNotesEnabled: false,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: false,
    canPlanFutureAssignments: false,
    clientIntake: 'daAdolescentClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    hideSessionsPageScheduleButton: false,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [],
    programsToDisplay: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
    ],
    programTitleOverride: {
      [ProgramCustomerPropertyId.alcoholUseDisorderTherapy]: 'Renew for Alcohol/Substance Use Disorder',
    },
    combineProgramSectionsAndShowReferral: true,
    recommendedEpisodeLength: 10,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    shouldConfirmSameSessionAssignment: false,
    showSessionLimit: false,
  },
  DirectAccessIndividualChildTherapy: {
    activityActions: {
      copy: false,
      edit: false,
      move: false,
      unassign: false,
    },
    aiNotesEnabled: false,
    appointmentDurationOptions: [50],
    autoAssignOnSessionClose: false,
    canPlanFutureAssignments: false,
    clientIntake: 'daChildClientIntake',
    defaultAppointmentDuration: 50,
    displayCloseEpisodeBtn: false,
    enhancedLCxDiagnosisEnabled: false,
    hideSessionsPageScheduleButton: false,
    icon: 'BlendedCareTherapyIcon',
    maxCapacity: 30,
    programBookingLinksAllowed: [],
    programsToDisplay: [
      ProgramCustomerPropertyId.considerLyraTherapy,
      ProgramCustomerPropertyId.stressManagement,
      ProgramCustomerPropertyId.blendedCareMeds,
      ProgramCustomerPropertyId.alcoholUseDisorderTherapy,
      ProgramCustomerPropertyId.clinicalLeaveEvaluation,
    ],
    programTitleOverride: {
      [ProgramCustomerPropertyId.alcoholUseDisorderTherapy]: 'Renew for Alcohol/Substance Use Disorder',
    },
    combineProgramSectionsAndShowReferral: true,
    recommendedEpisodeLength: 10,
    sessionFrequencyOptions: [
      sessionFrequencyOptions[SESSION_FREQUENCY.NO_REPEAT],
      sessionFrequencyOptions[SESSION_FREQUENCY.WEEKLY],
      sessionFrequencyOptions[SESSION_FREQUENCY.BIWEEKLY],
    ],
    sessionLabel: 'Session',
    sessionClosureGuide:
      'https://blended-care-physicians-hub.zendesk.com/hc/en-us/articles/5458667903635-BCM-Session-Closure-Guide',
    shouldConfirmSameSessionAssignment: false,
    showSessionLimit: false,
  },
}

export const paymentPreferences = { 2: 'ACH' }

export const programs: Record<LTProgramName, string> = {
  MedicationManagement: 'MedicationManagement',
  BlendedCareTherapy: 'BlendedCareTherapy',
  Coaching: 'Coaching',
  SingleSessionCoaching: 'SingleSessionCoaching',
  CoachingForParents: 'CoachingForParents',
  ManagerCoaching: 'ManagerCoaching',
  AlcoholUseDisorderTherapy: 'AlcoholUseDisorderTherapy',
  ClinicalLeaveEvaluation: 'ClinicalLeaveEvaluation',
  SubstanceUseEvaluation: 'SubstanceUseEvaluation',
  TeensTherapy: 'TeensTherapy',
  DialecticalBehaviorTherapy: 'DialecticalBehaviorTherapy',
  DialecticalBehaviorTherapyTeens: 'DialecticalBehaviorTherapyTeens',
  NeurodiversitySkillsCoaching: 'NeurodiversitySkillsCoaching',
  CaregiverTrainingForNeuro: 'CaregiverTrainingForNeuro',
  DirectAccessCouplesTherapy: 'DirectAccessCouplesTherapy',
  DirectAccessIndividualTherapy: 'DirectAccessIndividualTherapy',
  DirectAccessFamilyTherapy: 'DirectAccessFamilyTherapy',
  DirectAccessIndividualAdolescentTherapy: 'DirectAccessIndividualAdolescentTherapy',
  DirectAccessIndividualChildTherapy: 'DirectAccessIndividualChildTherapy',
  IcasIndividualTherapy: 'IcasIndividualTherapy',
}

export const customerExclusionList = [
  'walmartisrael',
  'qualcommglobal',
  'intuit-seasonal',
  'amazonkids',
  'lyraselect',
  'lyraselecthpi',
]

/**
 * This creates a <value>: <label> map from the above `diagnoses` array.
 * It has the following format:
 *
 *   {
 *    adhd: 'ADHD',
 *    anxiety: 'Anxiety',
 *    behavioral_problems: 'Behavioral Problems',
 *    ....
 *   }
 *
 * It is useful to easily display Diagnoses labels from an api response since the server return the `values`.
 */
export const diagnosesMap = diagnoses.reduce(
  (obj, { label, value }) => {
    obj[value] = label
    return obj
  },
  {} as Record<string, string>,
)

export const outcomesEligibleSessionTypes = {
  individual_initial_evaluation: 'Individual Initial Evaluation',
  individual_follow_up_session: 'Individual Follow-Up Session',
  couples_initial_evaluation: 'Couples Initial Evaluation',
  couples_follow_up_session: 'Couples Follow-Up Session',
  medication_management_initial_evaluation: 'Medication Management Initial Evaluation',
  medication_management_follow_up_session: 'Medication Management Follow-Up Evaluation',
  medication_management_follow_up_low_complexity:
    'Medication Management Follow-Up Evaluation (low complexity/20-29 min)',
  medication_management_follow_up_moderate_complexity:
    'Medication Management Follow-Up Evaluation (moderate complexity/30-39 min)',
  medication_management_follow_up_high_complexity:
    'Medication Management Follow-Up Evaluation (high complexity/40+ min)',
}

// Session types for DA providers
export const sessionTypes = {
  ...outcomesEligibleSessionTypes,
  minor_medication_management_initial_evaluation: 'Minor Medication Management Initial Evaluation',
  minor_medication_management_follow_up_evaluation: 'Minor Medication Management Follow-Up Evaluation',
  minor_medication_management_follow_up_low_complexity:
    'Minor Medication Management Follow-Up Evaluation (low complexity/20-29 min)',
  minor_medication_management_follow_up_moderate_complexity:
    'Minor Medication Management Follow-Up Evaluation (moderate complexity/30-39 min)',
  minor_medication_management_follow_up_high_complexity:
    'Minor Medication Management Follow-Up Evaluation (high complexity/40+ min)',
  family_initial_evaluation: 'Family Initial Evaluation',
  family_follow_up_session: 'Family Follow-Up Session',
  group_initial_evaluation: 'Group Initial Evaluation',
  group_follow_up_session: 'Group Follow-Up Session',
  dbt_group_session: 'Lyra DBT Group Session',
  smp_session: 'SMP Session',
  child_initial_evaluation: 'Minor Initial Evaluation',
  child_follow_up_session: 'Minor Follow-Up Session',
  lyra_therapy_teens_parent_session: 'LCT for Teen Parent Followup Session',
  dbt_teens_intake_session: 'DBT Teens Intake Session',
  dbt_teens_followup_session: 'DBT Teens Follow-Up Session',
  dbt_teens_caregiver_session: 'DBT Teens Caregiver Session',
  dbt_teens_family_session: 'DBT Teens Family Session',
  dbt_teens_group_session: 'DBT Teens Group Session',
  dbt_teens_medication_initial_session: 'DBT Teens Medication Initial Session',
  dbt_teens_medication_followup_session: 'DBT Teens Medication Follow-Up Session',
  caregiver_training_for_neuro_session: 'Caregiver Training Session',
  pediatric_behavioral_health_urgent_care_session: 'Pediatric Behavioral Health Urgent Care Session',
  other: 'Other',
}

const sessionTypesDisplayOrder: (keyof typeof sessionTypes)[] = [
  'individual_initial_evaluation',
  'individual_follow_up_session',
  'couples_initial_evaluation',
  'couples_follow_up_session',
  'family_initial_evaluation',
  'family_follow_up_session',
  'group_initial_evaluation',
  'group_follow_up_session',
  'dbt_group_session',
  'lyra_therapy_teens_parent_session',
  'dbt_teens_caregiver_session',
  'dbt_teens_group_session',
  'medication_management_initial_evaluation',
  'medication_management_follow_up_session',
  'medication_management_follow_up_low_complexity',
  'medication_management_follow_up_moderate_complexity',
  'medication_management_follow_up_high_complexity',
  'smp_session',
  'child_initial_evaluation',
  'child_follow_up_session',
  'minor_medication_management_initial_evaluation',
  'minor_medication_management_follow_up_evaluation',
  'minor_medication_management_follow_up_low_complexity',
  'minor_medication_management_follow_up_moderate_complexity',
  'minor_medication_management_follow_up_high_complexity',
  'caregiver_training_for_neuro_session',
  'pediatric_behavioral_health_urgent_care_session',
  'other',
]

export const sessionTypesDisplay = sessionTypesDisplayOrder.map((sessionTypeKey) => ({
  value: sessionTypeKey,
  label: sessionTypes[sessionTypeKey],
}))

export const coachingSessionTypesSet = new Set<string>(Object.values(CoachingSessionTypes))

// Meeting formats for creating a new session with a client
export const getMeetingFormats = ({ exclusions = [] }: { exclusions?: string[] } = {}) => {
  const defaultFormats = [
    { value: MEETING_FORMATS.VIDEO, text: 'Video session' },
    { value: MEETING_FORMATS.PHONE, text: 'Phone session' },
    { value: MEETING_FORMATS.LIVE_MESSAGING, text: 'Live messaging session' },
    { value: MEETING_FORMATS.IN_PERSON, text: 'In-person session' },
  ]

  return defaultFormats.filter((format) => !exclusions.includes(format.value))
}

// mapping with LW meeting format enums with PP Charge modalities
export const meetingFormatChargeModalityMap = {
  phone: 'tele_therapy',
  video: 'video',
  live_messaging: 'live_messaging',
  inPerson: 'in_person',
}

// Those are the attendance values expected and returned by the server
export const providerUnableToAttendReasonCancellation = (inLateCancelWindow = false) => {
  return [
    ...(inLateCancelWindow
      ? [
          {
            text: 'Rescheduled within 24 Hours: I have rescheduled this session within 24 hours of the session start time',
            value: 'providerUnableToAttendReasonRescheduledWithin24hours',
          },
        ]
      : []),
    {
      text: 'Planned time off: I have planned time off that has been submitted through Workday for manager/supervisor approval',
      value: 'providerUnableToAttendReasonPlannedAndApproved',
    },
    {
      text: 'Unplanned time off: I have unplanned time off that has been submitted through Workday for manager/supervisor approval',
      value: 'providerUnableToAttendReasonUnplannedAndSubmittedForApproval',
    },
    {
      text: 'Leave of Absence: I will be on a leave of absence that the Leaves & Accommodations Team has approved',
      value: 'providerUnableToAttendReasonAbsenceApprovedByLeavesAndAccomodationsTeam',
    },
    ...(inLateCancelWindow
      ? [
          {
            text: 'Severe weather: I am experiencing a severe weather emergency',
            value: 'providerUnableToAttendReasonSevereWeather',
          },
          {
            text: 'Client emergency: Another client has a time-sensitive clinical emergency',
            value: 'providerUnableToAttendReasonOtherClientEmergency',
          },
        ]
      : []),
    {
      text: `Other: There was another reason which I will describe below`,
      value: 'providerUnableToAttendReasonOtherReason',
    },
  ]
}

// Appointment Statuses used by BE
export const appointmentStatuses = {
  missed: 'missed',
  completed: 'completed',
  canceled: 'canceled',
}

export const changeNoticeReasons = (clientName: string) => {
  return [
    {
      text: `${clientName} was unable to attend the session due to a personal / family / medical emergency (e.g. a trip to the ER)`,
      value: 'medicalEmergency',
    },
    {
      text: `Technical issues (for either ${clientName} or me) prevented this session from happening`,
      value: 'technicalIssues',
    },
    {
      text: `${clientName} canceled within 24 hours`,
      value: 'clientInitiatedLate',
    },
  ]
}

export const attendanceOptions = {
  [attendanceValues.ATTENDED]: 'Attended',
  [attendanceValues.LAST_MIN_CANCEL]: 'Last Minute Cancellation',
  [attendanceValues.NO_SHOW]: 'No Show',
}

export const modalityOptions = {
  in_person: 'In Person',
  video: 'Video',
  tele_therapy: 'Phone',
}

export const LEIDOS_CHAT_MODALITY = { live_messaging: 'Chat' }

export const defaultErrorMessages = {
  400: 'The request contained invalid data. Please double check the information provided and try again.',
  401: 'Authorization failed for the given request. Please make sure you are logged in.',
  404: 'Nothing found for the requested service',
  405: 'Failed to find the requested service',
  413: 'The request your are trying to send is too large. The limit is 20MB.',
  500: 'The server is having issues. Please try again later.',
  503: 'An error occurred in making the request. Please try again.',
  504: 'The request has timed out. Please try again.',
}

const isProduction = process.env.NODE_ENV === 'production'

export const mixpanelToken = isProduction
  ? '89e7dd4bd73cd0eb3d7ccfd692e6c1b8' // Production token
  : 'f5a4b848dfa645cc08cfd4aed4b618a2' // Staging token

export const mixpanelReplaysPercent = isProduction ? 100 : 0

export const oktaClientId = isProduction
  ? '0oawbk45nAjpwJCZ6296' // Production ID
  : '0oad75oobtrqZkHvh0h7' // Staging ID

export const oktaGoogleSSOClientId = isProduction
  ? '0oaln2yztCiSdSALC296' // Production ID
  : '0oadklcqs9O0wBwGU0h7' // Staging ID

export const oktaBaseUrl = isProduction
  ? 'https://lyrahealth.okta.com' // Production URL
  : 'https://lyrahealth.oktapreview.com' // Staging URL

export const stripeUrl = isProduction
  ? 'https://dashboard.stripe.com/connect/transfers/' // Production value
  : 'https://dashboard.stripe.com/test/connect/transfers/' // Staging value

export const LWUrl = (employer: string) => {
  let LWURL = `https://${employer}.lyrahealth.com`
  if (!isProduction) {
    const { origin } = window.location
    if (origin.includes('staging')) {
      LWURL = `https://${employer}.staging.lyrahealth.com`
    } else if (origin.includes('prerelease')) {
      LWURL = `https://${employer}.prerelease2.automation.lyrahealth.com`
    } else if (origin.includes('dev')) {
      LWURL = `https://${employer}.dev.lyrahealth.com`
    } else if (origin.includes('localhost')) {
      LWURL = `https://${employer}.localhost:4000`
    }
  }
  return LWURL
}

export const parentIntakeQualtricsUrl = (qualtricsId: string) => {
  if (isProduction) {
    return `https://lyrahealth.qualtrics.com/jfe/form/SV_6yhUKME0cOzt9d4?req=${qualtricsId}`
  } else {
    return `https://lyrahealth.qualtrics.com/jfe/form/SV_3xR2c5y1FMtarUG?req=${qualtricsId}`
  }
}

export const providerInitialDirectBookingLink = ({
  employer,
  providerId,
}: {
  employer: string
  providerId: string
}) => {
  return `${LWUrl(employer)}/secure/index/provider/${providerId}?limit=${btoa(
    format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX"),
  )}`
}

export const states = [
  {
    value: 'AL',
    text: 'Alabama',
  },
  {
    value: 'AK',
    text: 'Alaska',
  },
  {
    value: 'AS',
    text: 'American Samoa',
  },
  {
    value: 'AZ',
    text: 'Arizona',
  },
  {
    value: 'AR',
    text: 'Arkansas',
  },
  {
    value: 'CA',
    text: 'California',
  },
  {
    value: 'CO',
    text: 'Colorado',
  },
  {
    value: 'CT',
    text: 'Connecticut',
  },
  {
    value: 'DE',
    text: 'Delaware',
  },
  {
    value: 'DC',
    text: 'District of Columbia',
  },
  {
    value: 'FL',
    text: 'Florida',
  },
  {
    value: 'GA',
    text: 'Georgia',
  },
  {
    value: 'GU',
    text: 'Guam',
  },
  {
    value: 'HI',
    text: 'Hawaii',
  },
  {
    value: 'ID',
    text: 'Idaho',
  },
  {
    value: 'IL',
    text: 'Illinois',
  },
  {
    value: 'IN',
    text: 'Indiana',
  },
  {
    value: 'IA',
    text: 'Iowa',
  },
  {
    value: 'KS',
    text: 'Kansas',
  },
  {
    value: 'KY',
    text: 'Kentucky',
  },
  {
    value: 'LA',
    text: 'Louisiana',
  },
  {
    value: 'ME',
    text: 'Maine',
  },
  {
    value: 'MD',
    text: 'Maryland',
  },
  {
    value: 'MA',
    text: 'Massachusetts',
  },
  {
    value: 'MI',
    text: 'Michigan',
  },
  {
    value: 'MN',
    text: 'Minnesota',
  },
  {
    value: 'MS',
    text: 'Mississippi',
  },
  {
    value: 'MO',
    text: 'Missouri',
  },
  {
    value: 'MT',
    text: 'Montana',
  },
  {
    value: 'NA',
    text: 'National',
  },
  {
    value: 'NE',
    text: 'Nebraska',
  },
  {
    value: 'NV',
    text: 'Nevada',
  },
  {
    value: 'NH',
    text: 'New Hampshire',
  },
  {
    value: 'NJ',
    text: 'New Jersey',
  },
  {
    value: 'NM',
    text: 'New Mexico',
  },
  {
    value: 'NY',
    text: 'New York',
  },
  {
    value: 'NC',
    text: 'North Carolina',
  },
  {
    value: 'ND',
    text: 'North Dakota',
  },
  {
    value: 'MP',
    text: 'Northern Mariana Islands',
  },
  {
    value: 'OH',
    text: 'Ohio',
  },
  {
    value: 'OK',
    text: 'Oklahoma',
  },
  {
    value: 'OR',
    text: 'Oregon',
  },
  {
    value: 'PA',
    text: 'Pennsylvania',
  },
  {
    value: 'PR',
    text: 'Puerto Rico',
  },
  {
    value: 'RI',
    text: 'Rhode Island',
  },
  {
    value: 'SC',
    text: 'South Carolina',
  },
  {
    value: 'SD',
    text: 'South Dakota',
  },
  {
    value: 'TN',
    text: 'Tennessee',
  },
  {
    value: 'TX',
    text: 'Texas',
  },
  {
    value: 'UT',
    text: 'Utah',
  },
  {
    value: 'VT',
    text: 'Vermont',
  },
  {
    value: 'VI',
    text: 'Virgin Islands',
  },
  {
    value: 'VA',
    text: 'Virginia',
  },
  {
    value: 'WA',
    text: 'Washington',
  },
  {
    value: 'WV',
    text: 'West Virginia',
  },
  {
    value: 'WI',
    text: 'Wisconsin',
  },
  {
    value: 'WY',
    text: 'Wyoming',
  },
]

export const bannerMessages = {
  REGISTER_PRACTICE_SUCCESS: 'Thank you! You successfully registered!',
  REGISTER_PROVIDER_SUCCESS: 'Thank you! You successfully registered!',
  SUBMIT_PAYMENT_SUCCESS: 'Thank you! Your payment was successfully submitted',
  PAYMENT_APPROVED: 'Payment approved.',
  PAYMENT_REJECTED: 'Payment rejected.',
  ADD_PROVIDER_SUCCESS: 'The provider specified has been added to this practice.',
  UPDATE_AVAILABILITY: 'Thank you for updating your availability',
  ASSESSMENT_TRACK_SUCCESS: 'Assessment track changed',
  PAYMENT_HISTORY_EMAIL_SENT: 'An email was sent to you regarding your requested payment history CSV',
}

export const sessionTracking = {
  WARNING_THRESHOLD: 5,
}

// Enum representing potential operations that can be performed on an appointment.
// This defines one source of truth for components handling functionality for both
// creating appointments and rescheduling appointments.
export enum APPOINTMENT_OPERATIONS {
  NEW = 'new',
  RESCHEDULE = 'reschedule',
  CANCEL = 'cancel',
}

export const LT_PROVIDER_MAX_CAPACITY = 13

export const INCREASED_PROVIDER_MAX_CAPACITY = 30

export const episodeTerminationStatusesEngageDA = [
  {
    text: 'Completed - Client is ready to complete care based on goal progress.',
    value: 'completed',
    name: 'Completed',
    order: 1,
  },
  {
    text: 'Never engaged - Client booked an appointment but no-showed and was not referred to other care.',
    value: 'not_initiated',
    name: 'Never engaged',
    order: 2,
  },
  {
    text: 'Drop out - Client attended at least 1 session and stopped care without progress on goals. Includes clients who are unresponsive or declined further sessions.',
    value: 'drop_out',
    name: 'Drop out',
    order: 3,
  },
  {
    text: 'Care transition - Client is transitioning care to a different Lyra provider or service.',
    value: 'care_transition',
    name: 'Care transition',
    order: 4,
  },
  {
    text: 'Client Request not Supported by the Lyra Benefit - Client is looking for other support not covered by the Lyra benefit.',
    value: 'not_supported',
    name: 'Client Request not Supported by the Lyra Benefit',
    order: 5,
  },
  {
    text: 'Not eligible - Client is not eligible for the Lyra benefit or has utilized all their covered sessions for the year.',
    value: 'not_eligible',
    name: 'Not eligible',
    order: 6,
  },
  {
    text: 'Booster session - For returning clients only. The client was inactive and returned for a single care session ONLY.',
    value: 'booster',
    name: 'Booster session',
    order: 7,
  },
]

export const episodeTerminationStatuses = [
  {
    text: 'Never engaged - Client booked an appointment but never showed up for any appointments and was not referred to other care.',
    value: 'not_initiated',
    name: 'Never engaged',
    order: 1,
  },
  {
    text: 'Client request not supported by any Lyra services - Client is looking for other support not covered by Blended Care programs and other Lyra services.',
    value: 'not_supported',
    name: 'Client request not supported by any Lyra services',
    order: 2,
  },
  {
    text: 'Care transition - Client is not completing care with me and is transitioning to a different Blended Care provider or different care type within Lyra.',
    value: 'care_transition',
    name: 'Care transition',
    order: 3,
  },
  {
    text: 'Drop out - Client attended at least 1 session and stopped care without having made progress on their goals. This can include clients who have been unresponsive to outreach attempts or declined further sessions.',
    value: 'drop_out',
    name: 'Drop out',
    order: 4,
  },
  {
    text: 'No longer eligible - Client is losing eligibility for the Blended Care program and other Lyra services or has utilized all their sessions for the year.',
    value: 'not_eligible',
    name: 'No longer eligible',
    order: 5,
  },
  {
    text: 'Completed - Client demonstrated readiness to complete care based on progress made on their goals.',
    value: 'completed',
    name: 'Completed',
    order: 6,
  },
  {
    text: 'Booster session - This client was inactive and returned for a single care session ONLY. I am ending this episode.',
    value: 'booster',
    name: 'Booster session',
    order: 7,
  },
]

export const specialityCareAttestationStatuses = {
  NO_ATTESTATION: 'no_attestation',
  APPROACHING_ATTESTATION: 'approaching_attestation',
  ATTESTATION_PRIOR_AUTHORIZATION: 'attestation_pre_authorization',
  ATTESTATION_NEEDED: 'attestation_needed',
}

export const healthPlanStates = {
  READY: 'health_plan_ready',
  NOT_READY: 'health_plan_not_ready',
  IN_USE: 'health_plan_in_use',
  NOT_ELIGIBLE: 'health_plan_not_eligible',
  LIMIT_NOT_REACHED: 'health_plan_warning_limit_not_reached',
  HEALTH_PLAN_PAYMENT_MISSING: 'health_plan_payment_missing',
}

export const episodeStates = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  TERMINATED: 'terminated',
  TERMINATED_NON_CLINICAL: 'terminated_non_clinical',
  TERMINATED_CLINICAL: 'terminated_clinical',
  NOT_INITIATED: 'not_initiated',
}

export const episodeProgress = {
  ALL: 'All',
  INTAKE_ONLY: 'Intake only',
  FOLLOWUP_ONLY: 'Follow-up only',
  OVER_EPISODE_COUNT: 'Over episode count',
  LONGER_EPISODES: 'Longer episodes',
}

export const selfHarmDashletTabs = {
  ADDRESSED: 'Addressed',
  NEW: 'New',
}

export const selfHarmTasksStates = {
  ACKNOWLEDGED: 'acknowledged',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  REQUIRES_ACKNOWLEDGMENT: 'requires_acknowledgment',
}

const PAYMENT_FORM_FIELDS = {
  PRACTICE_ID: 'practice_id',
  COMPANY: 'company',
  LYRA_CODE: 'lyra_code',
  RELATIONSHIP: 'relationship',
  DATE_OF_BIRTH: 'date_of_birth',
  PROVIDER_LYRA_ID: 'provider_lyra_id',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  ELIGIBLE_MEMBER_FIRST_NAME: 'eligible_member_first_name',
  ELIGIBLE_MEMBER_LAST_NAME: 'eligible_member_last_name',
  ELIGIBLE_MEMBER_DOB: 'eligible_member_dob',
  VISIT_DATE: 'visit_date',
  SESSION_TYPE: 'session_type',
  OTHER_SESSION_DESCRIPTION: 'other_session_description',
  SESSION_DURATION: 'session_duration',
  SESSION_RATE: 'session_rate',
  DIAGNOSIS: 'diagnosis',
  OTHER_DIAGNOSIS: 'other_diagnosis',
  ATTENDANCE: 'attendance',
  MODALITY: 'modality',
  IS_GUARANTEED_TIME: 'is_guaranteed_time',
  GUARANTEED_TIME_SESSION_TYPE: 'guaranteed_time_session_type',
  LAST_SESSION: 'last_session',
  LAST_SESSION_TERMINATED: 'last_session_terminated',
  NOTES: 'notes',
  REFERENCE_NUMBER: 'reference_number',
  LOCATION: 'location',
  PRIMARY_CONDITION: 'primary_condition',
  SECONDARY_CONDITION: 'secondary_condition',
  OTHER_PRIMARY_DESCRIPTION: 'other_primary_description',
  OTHER_SECONDARY_DESCRIPTION: 'other_secondary_description',
  PRIMARY_CONDITION_SEVERITY: 'primary_condition_severity',
  EBT_TYPE: 'ebt_type',
  OTHER_EBT_DESCRIPTION: 'other_ebt_description',
}

export const PAYMENT_FORM = {
  NAME: 'requestPaymentForm',
  FIELDS: PAYMENT_FORM_FIELDS,
  DEFAULT: {
    initialValues: {
      [PAYMENT_FORM_FIELDS.IS_GUARANTEED_TIME]: false,
      [PAYMENT_FORM_FIELDS.ATTENDANCE]: 'attended',
      [PAYMENT_FORM_FIELDS.LAST_SESSION]: false,
      [PAYMENT_FORM_FIELDS.LAST_SESSION_TERMINATED]: false,
      [PAYMENT_FORM_FIELDS.RELATIONSHIP]: 'employee',
      [PAYMENT_FORM_FIELDS.COMPANY]: [],
      [PAYMENT_FORM_FIELDS.DIAGNOSIS]: [],
      [PAYMENT_FORM_FIELDS.PRIMARY_CONDITION]: [],
    },
  },
  GUARANTEED_TIME: {
    initialValues: {
      [PAYMENT_FORM_FIELDS.IS_GUARANTEED_TIME]: true,
      [PAYMENT_FORM_FIELDS.FIRST_NAME]: 'Guaranteed',
      [PAYMENT_FORM_FIELDS.LAST_NAME]: 'Time',
      [PAYMENT_FORM_FIELDS.DATE_OF_BIRTH]: '01/01/2000',
      [PAYMENT_FORM_FIELDS.MODALITY]: 'in_person',
    },
    disabledFields: {
      [PAYMENT_FORM_FIELDS.FIRST_NAME]: true,
      [PAYMENT_FORM_FIELDS.LAST_NAME]: true,
      [PAYMENT_FORM_FIELDS.DATE_OF_BIRTH]: true,
      [PAYMENT_FORM_FIELDS.DIAGNOSIS]: true,
      [PAYMENT_FORM_FIELDS.ATTENDANCE]: true,
      [PAYMENT_FORM_FIELDS.MODALITY]: true,
      [PAYMENT_FORM_FIELDS.LOCATION]: true,
      [PAYMENT_FORM_FIELDS.LAST_SESSION]: true,
      [PAYMENT_FORM_FIELDS.LAST_SESSION_TERMINATED]: true,
      [PAYMENT_FORM_FIELDS.PRIMARY_CONDITION]: true,
      [PAYMENT_FORM_FIELDS.SECONDARY_CONDITION]: true,
      [PAYMENT_FORM_FIELDS.PRIMARY_CONDITION_SEVERITY]: true,
      [PAYMENT_FORM_FIELDS.EBT_TYPE]: true,
    },
  },
  THERAPIST: {
    initialValues: {},
    disabledFields: {},
  },
  THERAPIST_GUARANTEED_TIME: {
    initialValues: {
      [PAYMENT_FORM_FIELDS.SESSION_TYPE]: 'individual_follow_up_session',
    },
    disabledFields: {
      [PAYMENT_FORM_FIELDS.SESSION_TYPE]: true,
    },
  },
}

/**
 * Fields relevant to Leidos payment submissions.
 * When adding new form fields: if Leidos users need it, add it here.
 * Fields NOT in this set have their validation errors stripped in requestPaymentValidate.
 *
 * Note: DATE_OF_BIRTH is intentionally excluded. It is hidden from the UI for Leidos but
 * auto-populated behind the scenes via useLeidosLyraCodeDOB, so its validation errors should
 * be stripped (the user can't see or fix the field).
 */
export const LEIDOS_FORM_FIELDS = new Set([
  PAYMENT_FORM_FIELDS.PRACTICE_ID,
  PAYMENT_FORM_FIELDS.PROVIDER_LYRA_ID,
  PAYMENT_FORM_FIELDS.COMPANY,
  PAYMENT_FORM_FIELDS.LYRA_CODE,
  PAYMENT_FORM_FIELDS.FIRST_NAME,
  PAYMENT_FORM_FIELDS.LAST_NAME,
  PAYMENT_FORM_FIELDS.VISIT_DATE,
  PAYMENT_FORM_FIELDS.SESSION_TYPE,
  PAYMENT_FORM_FIELDS.SESSION_DURATION,
  PAYMENT_FORM_FIELDS.SESSION_RATE,
  PAYMENT_FORM_FIELDS.MODALITY,
  PAYMENT_FORM_FIELDS.ATTENDANCE,
  PAYMENT_FORM_FIELDS.REFERENCE_NUMBER,
  PAYMENT_FORM_FIELDS.LAST_SESSION,
  PAYMENT_FORM_FIELDS.LAST_SESSION_TERMINATED,
])

// The period of time (in days) after which we consider provider's availability outdated and force to update
export const AVAILABILITY_CONSIDERED_FRESH_DAYS = { [ROLES.INDIVIDUAL_PROVIDER]: 7, [ROLES.PRACTICE_PROVIDER]: 14 }

export const LYRA_CODE_LENGTH = 9

const allSessionTypes = uniq([...Object.keys(sessionTypes), ...Object.values(LTSessionTypes)])

export const CHECKBOX_GROUP_SESSION_TYPE_FILTER = {
  filterKey: 'session_type',
  data: allSessionTypes.map((key, group) => ({ id: key, text: key, group })),
}

export const CHECKBOX_GROUP_INVOICE_TYPE_FILTER = {
  filterKey: 'is_health_plan',
  data: [
    {
      id: 'HP',
      text: 'HP',
      group: 0,
    },
    {
      id: 'EAP',
      text: 'EAP',
      group: 1,
    },
  ],
}

export const CHECKBOX_GROUP_PAYMENT_STATUS_FILTER = {
  filterKey: 'status',
  data: [
    {
      id: 'submitted',
      text: 'Submitted',
      group: 0,
    },
    {
      id: 'pending',
      text: 'Pending',
      group: 1,
    },
    {
      id: 'approved',
      text: 'Approved',
      group: 2,
    },
    {
      id: 'rejected',
      text: 'Rejected',
      group: 3,
    },
    {
      id: 'cancelled',
      text: 'Cancelled',
      group: 4,
    },
    {
      id: 't0_sessions',
      text: 'T0 Sessions',
      group: 5,
    },
    {
      id: 'reversed',
      text: 'Reversed',
      group: 6,
    },
    {
      id: 'pending_note',
      text: 'Pending Note',
      group: 7,
    },
  ],
}

export const CHECKBOX_GROUP_ELIGIBILITY_STATUS_FILTER = {
  filterKey: 'eligibility_code',
  data: [
    {
      id: '100',
      text: 'Eligiblity True',
      group: 0,
    },
    {
      id: '101',
      text: 'Eligiblity False: No customer eligibility file',
      group: 1,
    },
    {
      id: '102',
      text: 'Eligibility False: Member not found',
      group: 2,
    },
    {
      id: '103',
      text: 'Eligibility False: Not Eligible at session date',
      group: 3,
    },
    {
      id: '104',
      text: 'Eligibility False: Eligible with another company',
      group: 4,
    },
    {
      id: '105',
      text: 'Eligibility False: Member found in another company but not currently eligible',
      group: 5,
    },
    {
      id: '0',
      text: 'Eligibility False: Unknown - Historical Data',
      group: 6,
    },
    {
      id: '1',
      text: 'Eligibility False: Unknown - RTE Service Error',
      group: 7,
    },
  ],
}

export const INVOICE_NUMBER_PER_PAGE = 100

export const paymentRejectionReasons = {
  'Incorrect Patient Name': 'Incorrect Patient Name',
  'Incorrect Eligible Member Name': 'Incorrect Eligible Member Name',
  'Incorrect session type for missed appointment':
    'Please resubmit under the appropriate pre-populated session type and selected “No Show” or “Last Minute Cancellation” for Attendance',
  'Patient First and Last Names Reversed': 'Patient First and Last Names Reversed',
  'Unable to Match Patient Date of Birth': 'Unable to Match Patient Date of Birth',
  'Unable to Match Eligible Member Date of Birth': 'Unable to Match Eligible Member Date of Birth',
  'Incorrect Sponsoring Company': 'Incorrect Sponsoring Company',
  'Please Include Eligible Member Information': 'Please Include Eligible Member Information',
  'Unable to Identify Client':
    'Unable to Identify Client. Client may not be eligible. Please contact client and confirm full legal name, date of birth, and that they are still eligible for Lyra benefits through their sponsoring company. If client receives Lyra through another eligible member, please indicate that patient is a dependent and provide eligible member information.',
  'Unable to Identify Eligible Member':
    'Unable to Identify Eligible Member. Client may not be eligible. Please contact client and confirm full legal name, date of birth, and that they are still eligible for Lyra benefits through their sponsoring company.',
  'Not eligible at session date':
    'Not eligible at session date. Please contact client and confirm full legal name, date of birth, and/or possible employment status changes. If client receives Lyra through another eligible member, please indicate that patient is a dependent and provide eligible member information.',
  'Client is not eligible':
    'Client is not eligible for Lyra benefits at this session date and will be responsible for the cost of this session.',
  'Missing ICD-10': 'Please Re-Submit With ICD-10 Code And Co-Payment Consent',
  'Non-Contracted Service/Session Rate': 'Non-Contracted Service/Session Rate',
  'Duplicate Invoice': 'Duplicate Invoice',
  'Meds are not covered': 'This employer does not cover Medication Management through Lyra',
  'No session occurred':
    'In the future, please email providers@lyrahealth.com to notify us of treatment termination for your clients without a session occurring. Submitting an invoice will charge the client a session.',
  'Test Client': 'Test Client',
  'Resubmit as Individual Follow Up': 'Please resubmit under session type "Individual Follow Up"',
  'Resubmit as Couples': 'Please resubmit as a "Couples" session type.',
  'Resubmit as Med Management': 'Please resubmit as a "Medication Management" session type.',
  'Reach out for further instructions':
    'Please reach out to providers@lyrahealth.com for assistance with submitting this invoice.',
  'EMDR Diagnosis does not contain trauma/PTSD':
    'Lyra is only able to reimburse for evidence-based therapies (EBTs) as defined by the American Psychological Association’s (APA) Division 12, the Society for Clinical Psychology. At this time, EMDR is only recognized as an EBT for PTSD/trauma and as such we will not be able to approve this service.',
  'Session length exceeds 90 minutes':
    'Lyra is unable to reimburse for sessions that exceed 90 minutes. Extended sessions must be clinically indicated and approved by providers@lyrahealth.com.',
  'Client not approved for extended session':
    'Extended sessions must be clinically indicated and approved by providers@lyrahealth.com.',
  'Multiple sessions in one day/Non-approved extended session':
    'As indicated in your contract with Lyra, therapy sessions should be delivered at a frequency of no more than once a week for a session duration of 45-50 minutes. Multiple sessions in the same day must be clinically indicated and approved by providers@lyrahealth.com.',
  'Non-approved multiple couples sessions in a week':
    'As indicated in your contract with Lyra, therapy sessions should be delivered at a frequency of no more than once a week. Multiple sessions in the same week must be clinically indicated and approved by providers@lyrahealth.com.',
  'Double/extended group session':
    'As indicated in your contract with Lyra, therapy sessions should be delivered at a frequency of no more than once a week.',
  'Collateral session submitted with family member as patient':
    'Please resubmit as a follow up session using the client’s name who is receiving treatment as the client on the invoice',
  'Non-emergency short session':
    'As indicated in your contract with Lyra, therapy sessions should be delivered with a session duration of 45-50 minutes. Please do not charge the client.',
}

export const paymentReversalReasons = {
  'Accidentally Approved': 'Invoice was incorrectly approved.',
  'Billed in Error': 'This invoice was billed in error.',
  Duplicate: 'This invoice is a duplicate.',
  'Not Attended/Session did not occur': 'This session did not occur.',
  'Provider Request - Unspecified': 'We received a request to reverse this invoice.',
  'Tech Issues': 'The client was experiencing technical issues.',
  'Wrong Group Provider': 'The invoice was submitted under the wrong group provider.',
  'Wrong Sponsoring Company': 'The invoice lists the incorrect sponsoring company.',
  'Wrong Client': 'The invoice lists the incorrect client.',
  'Wrong DOS': 'The invoice lists the incorrect date of service.',
  'Wrong Session Type': 'The invoice lists the incorrect session type.',
  'Wrong Session Length': 'The invoice lists the incorrect session length.',
  'Wrong Rate': 'The invoice was approved at an incorrect rate.',
  'Wrong Modality': 'The invoice lists the incorrect session modality.',
  'Wrong Attendance': 'The invoice lists the incorrect attendance.',
  'Wrong Diagnosis': 'The invoice lists the incorrect diagnosis.',
  'Discontinue Care': 'The invoice incorrectly identifies (dis)continuation of care.',
  'Provider License Expired':
    'The license Lyra has on file for you has expired. Please contact credentials@lyrahealth.com with your updated license to continue seeing Lyra clients.',
  'Client out of Country': 'The client was not in the country, and therefore could not receive services.',
  'Internal Review': 'Invoice Reversed as a Result of an Internal Review.',
}

export const paymentExclusionReasons = {
  'Dual Eligibility - Same Sponsoring Company':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Dual Eligibility - Same Sponsoring Company',
  'End of Employment':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: End of Employment',
  'Grief & Loss':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Grief & Loss',
  'Monitored Referral':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Monitored Referral',
  Relocation:
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Relocation',
  'Sponsoring Company Request - Incident':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Sponsoring Company Request - Incident',
  'Sponsoring Company Request - Program Design':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Sponsoring Company Request - Program Design',
  'Sponsoring Company Request - Unspecified':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Sponsoring Company Request - Unspecified',
  'Trust & Safety':
    '***Internal Use Only*** This session was excluded from the client’s session count for the following reason: Trust & Safety',
}

export const companyMessages = {
  MULTIPLE_INSTANCES: 'multipleInstances',
  NUCLEAR_BADGED: 'nuclearBadged',
  LIMITED_ACCESS_AND_MODIFIED_REFERRALS: 'limitedAccessAndModifiedReferrals',
}

export const cloudfrontOriginUrl = isProduction
  ? 'https://provider.lyrahealth.com'
  : 'https://provider.staging.lyrahealth.com'

export const genAIBaseUrl = isProduction
  ? 'https://genai-gateway.lyrahealth.com'
  : 'https://genai-gateway.staging.lyrahealth.com'

export const affirmativeResponses = ['Y', 'y', 'Yes', 'yes', 'True', 'true', true, 1]

// TO-DO - switch this to the actual URLs when they come inz
export const measurementBasedCareUrls = {
  percentageHelpArticle:
    'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/360035387014-Measurement-Based-Care-Compensation-Criteria',
  countHelpArticle:
    'https://bct-lyrahealth.zendesk.com/hc/en-us/articles/360042313293-Caseload-Measurement-Based-Care-MBC-Compensation-Criteria#h_01GB90V32NGZG8EWWBT6WNK3NP',
}

const severityValues = {
  MILD: 'mild',
  MODERATE: 'moderate',
  SEVERE: 'severe',
}

export const severityOptions = {
  [severityValues.MILD]: 'Mild',
  [severityValues.MODERATE]: 'Moderate',
  [severityValues.SEVERE]: 'Severe',
}
export const ebtTypes = [
  { label: 'Acceptance and Commitment Therapy (ACT)', value: 'acceptance_commitment_therapy' },
  { label: 'Attachment Based Family Therapy', value: 'attachment_based_family_therapy' },
  { label: 'Behavior Management Therapy', value: 'behavior_management_therapy' },
  { label: 'Behavior Modification Therapy', value: 'behavior_modification_therapy' },
  { label: 'Behavioral Activation (BA)', value: 'behavioral_activation' },
  { label: 'Behavioral Couples Therapy', value: 'behavioral_couples_therapy' },
  { label: 'Behavioral Therapy', value: 'behavioral_therapy' },
  { label: 'Child-Parent Psychotherapy', value: 'child_parent_psychotherapy' },
  { label: 'Cognitive Behavioral Conjoint Therapy for PTSD', value: 'cognitive_behavioral_conjoint_therapy_ptsd' },
  { label: 'Cognitive Behavioral Therapy (CBT)', value: 'cognitive_behavioral_therapy' },
  { label: 'Cognitive Behavioral Therapy for Suicidality (CBT-SI)', value: 'cognitive_behavioral_therapy_suicidality' },
  { label: 'Cognitive Processing Therapy (CPT)', value: 'cognitive_processing_therapy' },
  { label: 'Cognitive Therapy', value: 'cognitive_therapy' },
  { label: 'Dialectical Behavior Therapy (DBT)', value: 'dialectical_behavior_therapy' },
  { label: 'Emotion Focused Couples Therapy (EFT)', value: 'emotion_focused_couples_therapy' },
  { label: 'Exposure and Response Prevention (ERP)', value: 'exposure_response_prevention' },
  { label: 'Eye Movement Desensitization and Reprocessing (EMDR)', value: 'eye_movement_desensitization_reprocessing' },
  { label: 'Family Focused Therapy (FFT)', value: 'family_focused_therapy' },
  { label: 'Family-Based Treatment (FBT) for Eating Disorders', value: 'family_based_treatment_eating_disorders' },
  { label: 'Gottman Couples Therapy', value: 'gottman_couples_therapy' },
  { label: 'Habit Reversal Training (HRT)', value: 'habit_reversal_training' },
  { label: 'Integrative Behavioral Couples Therapy (IBCT)', value: 'integrative_behavioral_couples_therapy' },
  { label: 'Intensive Short-Term Dynamic Psychotherapy (ISTDP)', value: 'intensive_short_term_dynamic_psychotherapy' },
  { label: 'Interpersonal and Social Rhythm Therapy (ISRT)', value: 'interpersonal_social_rhythm_therapy' },
  { label: 'Interpersonal Psychotherapy (IPT)', value: 'interpersonal_psychotherapy' },
  { label: 'Mindfulness Based Approaches', value: 'mindfulness_based_approaches' },
  { label: 'Mindfulness-Based Cognitive Therapy (MBCT)', value: 'mindfulness_based_cognitive_therapy' },
  { label: 'Motivational Interviewing', value: 'motivational_interviewing' },
  { label: 'Multidimensional Family Therapy', value: 'multidimensional_family_therapy' },
  { label: 'Multisystemic Therapy', value: 'multisystemic_therapy' },
  { label: 'Parent Management Training (PMT)', value: 'parent_management_training' },
  { label: 'Parent-Child Interaction Therapy', value: 'parent_child_interaction_therapy' },
  { label: 'Problem-Solving Therapy', value: 'problem_solving_therapy' },
  { label: 'Prolonged Exposure Therapy', value: 'prolonged_exposure_therapy' },
  { label: 'Seeking Safety', value: 'seeking_safety' },
  { label: 'SMART Recovery', value: 'smart_recovery' },
  { label: 'Social Skills Training (SST)', value: 'social_skills_training' },
  { label: 'Trauma Focused CBT (Child/Adolescent)', value: 'trauma_focused_cbt' },
  { label: 'Unified Protocol (UP)', value: 'unified_protocol' },
  { label: 'Other', value: 'other' },
]

export const getConditions = ({ includeDivorce = true }: { includeDivorce?: boolean }) => {
  const conditions = [
    { label: 'ADHD', value: 'adhd' },
    { label: 'Generalized Anxiety Disorder', value: 'generalized_anxiety_disorder' },
    { label: 'Social Anxiety Disorder', value: 'social_anxiety_disorder' },
    { label: 'Behavioral Problems', value: 'behavioral_problems' },
    { label: 'Bipolar Disorder', value: 'bipolar_disorder' },
    { label: 'Chronic Pain', value: 'chronic_pain' },
    { label: 'Depression/Sadness', value: 'depression_sadness' },
    { label: 'Dysthymic Disorder', value: 'dysthymic_disorder' },
    { label: 'Eating Issues', value: 'eating_issues' },
    { label: 'Grief', value: 'grief' },
    { label: 'OCD', value: 'ocd' },
    { label: 'Parenting / Child Behavior Issues', value: 'parenting_child_behavior_issues' },
    { label: 'Relationship Issues', value: 'relationship_issues' },
    { label: 'Separation or Divorce', value: 'divorce' },
    { label: 'Stress', value: 'stress' },
    { label: 'Life Transitions', value: 'life_transition' },
    { label: 'Career Issues', value: 'career_issues' },
    { label: 'Sleep Issues', value: 'sleep_issues' },
    { label: 'Alcohol Use', value: 'alcohol_use' },
    { label: 'Substance Use', value: 'substance_use' },
    { label: 'Self-Harm (Nonsuicidal Self-Harm)', value: 'self_harm' },
    { label: 'Suicidal Ideation', value: 'suicidal_ideation' },
    { label: 'Trauma/PTSD', value: 'trauma' },
    { label: 'Other', value: 'other' },
  ]
  return includeDivorce ? conditions : conditions.filter((condition) => condition.value !== 'divorce')
}

export const getFlexibleNoteConditions = () => [
  { label: 'Alcohol Use', value: 'alcohol_use' },
  { label: 'Anger management', value: 'anger_management' },
  { label: 'Anxiety symptoms', value: 'anxiety_symptoms' },
  { label: 'Attention and hyperactivity patterns', value: 'attention_and_hyperactivity_patterns' },
  { label: 'Behavioral dysregulation', value: 'behavioral_dysregulation' },
  { label: 'Body image concerns', value: 'body_image_concerns' },
  { label: 'Burnout', value: 'burnout' },
  { label: 'Career transitions', value: 'career_transitions' },
  { label: 'Caregiver burden', value: 'caregiver_burden' },
  { label: 'Chronic or major health conditions', value: 'chronic_or_major_health_conditions' },
  { label: 'Chronic pain', value: 'chronic_pain' },
  { label: 'Cyclical Mood or Energy Patterns', value: 'cyclical_mood_or_energy_patterns' },
  { label: 'Depressive symptoms', value: 'depressive_symptoms' },
  { label: 'Difficulty concentrating', value: 'difficulty_concentrating' },
  { label: 'Eating Issues', value: 'eating_issues' },
  { label: 'Excessive worrying', value: 'excessive_worrying' },
  { label: 'Fatigue and low energy', value: 'fatigue_and_low_energy' },
  { label: 'Feeling overwhelmed', value: 'feeling_overwhelmed' },
  { label: 'Feelings of guilt', value: 'feelings_of_guilt' },
  { label: 'Fertility journeys', value: 'fertility_journeys' },
  { label: 'Financial worries', value: 'financial_worries' },
  { label: 'Gambling behaviors', value: 'gambling_behaviors' },
  { label: 'Gender identity dysphoria', value: 'gender_identity_dysphoria' },
  { label: 'Grief or death of a loved one', value: 'grief_or_death_of_a_loved_one' },
  { label: 'Hopelessness', value: 'hopelessness' },
  { label: 'Imposter syndrome', value: 'imposter_syndrome' },
  { label: 'Intimate relationship conflict', value: 'intimate_relationship_conflict' },
  { label: 'Irritability', value: 'irritability' },
  { label: 'Lack of enjoyment', value: 'lack_of_enjoyment' },
  { label: 'Life transitions', value: 'life_transitions' },
  { label: 'Loneliness', value: 'loneliness' },
  { label: 'Low self-esteem', value: 'low_self_esteem' },
  { label: 'Menopause challenges', value: 'menopause_challenges' },
  { label: 'Nervousness or feeling on edge', value: 'nervousness_or_feeling_on_edge' },
  { label: 'Obsessive/compulsive challenges', value: 'obsessive_compulsive_challenges' },
  { label: 'Panic attacks', value: 'panic_attacks' },
  { label: 'Parenting stress', value: 'parenting_stress' },
  { label: 'Perfectionism challenges', value: 'perfectionism_challenges' },
  { label: 'Persistent Sadness', value: 'persistent_sadness' },
  { label: 'Pregnancy/new child adjustment', value: 'pregnancy_new_child_adjustment' },
  { label: 'Racial stress', value: 'racial_stress' },
  { label: 'Relationship issues', value: 'relationship_issues' },
  { label: 'Restlessness', value: 'restlessness' },
  { label: 'Self-harm', value: 'self_harm' },
  { label: 'Separation or divorce', value: 'separation_or_divorce' },
  { label: 'Sexual orientation stress', value: 'sexual_orientation_stress' },
  { label: 'Sleep disturbances', value: 'sleep_disturbances' },
  { label: 'Social anxiety and avoidance', value: 'social_anxiety_and_avoidance' },
  { label: 'Substance Use', value: 'substance_use' },
  { label: 'Suicidal thoughts', value: 'suicidal_thoughts' },
  { label: 'Traumatic experiences', value: 'traumatic_experiences' },
  { label: 'Trouble communicating', value: 'trouble_communicating' },
  { label: 'Trouble relaxing', value: 'trouble_relaxing' },
  { label: 'Work stress', value: 'work_stress' },
  { label: 'Worthlessness', value: 'worthlessness' },
  { label: 'Other', value: 'other' },
]

export const getEndOfSessionConditions = ({
  isProviderEngageDA = false,
  isDaFlexibleNote = false,
}: {
  isProviderEngageDA?: boolean
  isDaFlexibleNote?: boolean
}) => (isDaFlexibleNote ? getFlexibleNoteConditions() : getConditions({ includeDivorce: isProviderEngageDA }))

export const deprecatedConditions = [
  { label: 'Alcohol Use', value: 'alcohol_use_disorder' },
  { label: 'Generalized Anxiety Disorder', value: 'anxiety' },
  { label: 'Generalized Anxiety Disorder', value: 'anxiety_disorder' },
  { label: 'Behavioral Problems', value: 'child_conduct_behaviors' },
  { label: 'Depression/Sadness', value: 'depressive_disorder' },
  { label: 'Substance Use', value: 'substance_use_disorder' },
  { label: 'Suicidal Ideation', value: 'high_risk_safety_concerns' },
  { label: 'Relationship Issues', value: 'relationship_distress' },
  { label: 'Parenting / Child Behavior Issues', value: 'parenting_child_conflict' },
  { label: 'Other', value: 'psychosocial_adjustment' },
  { label: 'Other', value: 'psychotic_disorder' },
  { label: 'Trauma/PTSD', value: 'ptsd_trauma_related_disorder' },
  { label: 'Other', value: 'discriminatory_stress_trauma' },
  { label: 'Insomnia', value: 'sleep' },
  { label: 'Bipolar Disorder', value: 'bipolar_related_disorder' },
  { label: 'Eating Disorder', value: 'eating_food_related_disorder' },
  { label: 'OCD', value: 'ocd_compulsive_disorder' },
  { label: 'Substance Abuse', value: 'sud_addiction' },
  { label: 'Relationship Issues', value: 'marital_relationship_distress' },
  { label: 'Separation or Divorce', value: 'separation_divorce' },
]

export const demographicAgeMap = {
  Infant: 'Infant (0 -2)',
  'Younger Child': 'Younger Child (3-5)',
  'Early Child': 'Early Child (3-5)',
  Child: 'Child (6-9)',
  'Young Child': 'Young Child (0-5)',
  'Older Child': 'Older Child (6-11)',
  Adolescent: 'Adolescent (12-17)',
  'Early Adolescent': 'Early Adolescent (10-12)',
  'Middle Adolescent': 'Middle Adolescent (13-15)',
  'Late Adolescent': 'Late Adolescent (16-17)',
  Adult: 'Adult (18-64)',
  Senior: 'Senior (65+)',
}

export const DA_PROGRAM_TAXONOMY_TO_DISPLAY_VALUE = {
  [PROGRAM_TAXONOMY.clientele.couples]: {
    display: 'Couples Therapy',
  },
  [PROGRAM_TAXONOMY.clientele.family]: {
    display: 'Family Therapy',
  },
  [PROGRAM_TAXONOMY.clientele.individual]: {
    display: 'Individual Therapy',
  },
  [PROGRAM_TAXONOMY.clientele.group]: {
    display: 'Group Therapy',
  },
  [PROGRAM_TAXONOMY.treatment.medicationManagement]: {
    display: 'Medication Management',
  },
}

export const COACHING_HIDDEN_FILTER_CATEGORIES = ['Suicidal Ideation', 'OCD', 'PTSD', 'Disordered Eating']

export const programString = { eap: 'EAP', hp: 'Health Plan' }

export type ProgramStringKey = keyof typeof programString

export const coverageStates = {
  EAP: 'Your client can use EAP sessions.',
  EAP_HP: 'Your client can use free EAP sessions or an eligible health plan to pay for sessions.',
  EAP_SPECIALTY:
    'Your client can use EAP sessions. If they have an active substance use disorder, are at risk for hospitalization, or have treatment resistant conditions, they may continue past the recommended sessions.',
  EAP_HP_SPECIALTY:
    'Your client can use free EAP sessions or an eligible health plan to pay for sessions. If they have an active substance use disorder, are at risk for hospitalization, or have treatment resistant conditions, they may continue past the recommended sessions.',
  HP: 'Your client can use an eligible health plan to pay for sessions.',
  HP_SPECIALTY:
    'Your client can use an eligible health plan to pay for sessions. If they have an active substance use disorder, are at risk for hospitalization, or have treatment resistant conditions, they may continue past the recommended sessions',
  NICOTINE:
    'Your client is eligible at no additional cost. If your client has multiple substance use concerns, other co-occurring psychiatric needs, or has complex needs, please consider Renew rather than the Nicotine Quitline.',
  NICOTINE_RENEW_DISABLED:
    'Your client is eligible at no additional cost. If your client has multiple substance use concerns, other co-occurring psychiatric needs, or has complex needs, please consider therapy rather than the Nicotine Quitline.',
  SPECIALTY:
    'If your client has an active substance use disorder, is at risk for hospitalization, or has treatment resistant conditions, they may continue with sessions.',
  EAP_SELECT:
    'Your client can only use EAP sessions and is not eligible to continue care through Lyra past these sessions.',
}

export const LYRA_SELECT_ZENDESK_ARTICLE_URL =
  'https://blended-care.zendesk.com/hc/en-us/articles/40120429947411-Lyra-Select'

export const guaranteedTimeSessionTypes = [
  { label: 'Unused Time (contractors only)', value: 'unused_time' },
  { label: 'No Show or Late Cancellation', value: 'no_show_late_cancellation' },
  { label: 'Manager/Clinical Consultation', value: 'manager_clinical_consultation' },
  { label: 'Urgent/Same Day Assessment/Triage', value: 'urgent_assessment' },
  { label: 'Critical Incident Response', value: 'critical_incident_response' },
  { label: 'Customer Engagements', value: 'customer_engagements' },
]

export const PAYMENT_TYPES = {
  EAP: 'eap',
  HPI: 'hpi',
  HPI_INELIGIBLE: 'hpi_ineligible',
  SELF_PAY: 'self_pay',
  SELF_PAY_INELIGIBLE: 'self_pay_ineligible',
  NO_PAYMENT_OPTIONS: 'no_payment_options',
}

export const ESSENTIALS_BASE_URL = '/secure/essentials'
